// ============================================================================
// TagsBar.tsx — строка тегов в форме объекта (существующие записи):
// чипы + «+» (пикер). Сохранение — SetSubjectTags (полная замена), после —
// emitDataChanged (списки/карточки обновляются сразу).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { TagChips } from '@/components/tags/TagChips';
import { TagPickerDialog } from '@/components/tags/TagPickerDialog';
import { emitDataChanged } from '@/lib/dataEvents';
import { logErrorToJournal } from '@/lib/errors';
import { listSubjectTags, setSubjectTags, type TagInfo, type TagSubjectRef } from '@/lib/tags';

export function TagsBar({ subjectType, subjectCode, subjectId }: { subjectType: string; subjectCode: string; subjectId: string }) {
  const [tags, setTags] = useState<TagInfo[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const subjectRef = useCallback(
    (): TagSubjectRef => ({ subjectType, subjectCode, subjectId }),
    [subjectType, subjectCode, subjectId],
  );

  const reload = useCallback(() => {
    listSubjectTags(subjectRef())
      .then(setTags)
      .catch((e) => logErrorToJournal(e, 'tags.subject'));
  }, [subjectRef]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="flex items-center gap-2 flex-wrap px-0.5">
      <span className="text-[11px] text-muted-foreground shrink-0">Теги:</span>
      <TagChips tags={tags} />
      <button
        type="button"
        title="Изменить теги"
        className="h-5 w-5 rounded border border-border/60 flex items-center justify-center text-muted-foreground hover:bg-accent/40"
        onClick={() => setPickerOpen(true)}
      >
        <Plus className="h-3 w-3" />
      </button>
      <TagPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedIds={tags.map((t) => t.id)}
        onSave={async (ids) => {
          await setSubjectTags(subjectRef(), ids);
          reload();
          emitDataChanged({ table: subjectCode || subjectType, entityType: subjectType, entityCode: subjectCode, action: 'updated' });
        }}
      />
    </div>
  );
}
